import { getJwtToken, initializeJwtToken } from '../modules/jwtTokenManager.js';

const WS_DEBUG = false;
const MAX_TOKEN_RETRIES = 5;
const MAX_RETRY_DELAY = 30;
let tokenInitializationPromise = null;
const requestedWebSocketChannels = new Set();

function sendWebSocketRpc(ws, payload) {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
    }

    try {
        ws.send(JSON.stringify(payload));
    } catch (error) {
        wsLog('[WebSocket] Failed to send RPC', error);
    }
}

function subscribeToChannel(ws, channel) {
    if (!channel) {
        return;
    }

    sendWebSocketRpc(ws, {
        id: `subscribe:${channel}:${Date.now()}`,
        method: 'subscribe',
        parameters: [channel],
    });
}

function queueWebSocketChannelSubscription(channel) {
    if (!channel) {
        return;
    }

    requestedWebSocketChannels.add(channel);

    // Channels added to the set are subscribed on (re)connect via
    // subscribeToRequestedChannels. This same-tab signal lets a socket that is
    // already open subscribe live, since currentWs is local to $(document).ready.
    document.dispatchEvent(new CustomEvent('ws:subscribe-channel-request', {
        detail: { channel }
    }));
}

window.requestWebSocketChannelSubscription = queueWebSocketChannelSubscription;

function subscribeToRequestedChannels(ws) {
    requestedWebSocketChannels.forEach((channel) => {
        subscribeToChannel(ws, channel);
    });
}

async function getOrInitializeJwtToken() {
    const existingTokenData = getJwtToken();
    if (existingTokenData?.auth?.token) {
        return existingTokenData;
    }

    if (!tokenInitializationPromise) {
        tokenInitializationPromise = initializeJwtToken().finally(() => {
            tokenInitializationPromise = null;
        });
    }

    return tokenInitializationPromise;
}

function wsLog(message, data) {
    if (WS_DEBUG) {
        if (data !== undefined) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
}

// Tab title unread indicator: prepends "(N) " to document.title where N is
// the total count of *new* events (PMs, wall posts, friend requests, photo
// comments) that arrived since the user last looked at the
// tab — not the lifetime unread total. Matches Gmail/Slack/Discord: the
// badge answers "what's new since I looked away?", not "how big is my
// backlog?".
//
// "Not actively looking" means either:
//   - The tab is hidden (different tab in this window), or
//   - The window has lost OS focus (different window/app is active)
// The second case matters because Chrome keeps document.hidden=false for the
// front tab of a backgrounded window, so a hidden-only check misses the
// common multi-window workflow.
//
// Implementation: per-counter state. When the user looks away, snapshot the
// current value of each counter as its baseline. The badge shows the sum of
// (latest - baseline) across all tracked counters. When the user returns,
// clear baselines and re-snapshot on next blur.
const TRACKED_COUNTER_IDS = ['pmNewCnt', 'wpNewCnt', 'frNewCnt', 'phNewCnt'];

(function initUnreadTitleIndicator() {
    let baseTitle = null;
    const latestCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, 0]));
    const baselineCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, null]));

    // Strip any existing "(N) " prefix so re-renders don't compound.
    const stripPrefix = (title) => title.replace(/^\(\d+\)\s+/, '');

    const captureBaseTitle = () => {
        if (baseTitle === null) {
            baseTitle = stripPrefix(document.title);
        }
    };

    const totalDelta = () => TRACKED_COUNTER_IDS.reduce((sum, id) => {
        const base = baselineCount[id];
        return base === null ? sum : sum + Math.max(0, latestCount[id] - base);
    }, 0);

    const render = () => {
        if (baseTitle === null) {
            return;
        }
        const delta = totalDelta();
        document.title = delta > 0 ? `(${delta}) ${baseTitle}` : baseTitle;
    };

    // On /messages the page manages its own unread state (and knows whether
    // the user is viewing the active thread), so defer to it entirely.
    const isMessagesPage = () => /\/app\/messages(?:$|\/|\?)/.test(
        window.location.pathname + window.location.search
    );

    const isUserLookingAtTab = () => !document.hidden && document.hasFocus();

    const snapshotBaselinesIfNeeded = () => {
        // Take baseline the first time the user looks away. Subsequent counter
        // updates while still away accumulate against this baseline.
        for (const id of TRACKED_COUNTER_IDS) {
            if (baselineCount[id] === null) {
                baselineCount[id] = latestCount[id];
            }
        }
    };

    const clearAndResetBaselines = () => {
        for (const id of TRACKED_COUNTER_IDS) {
            baselineCount[id] = null;
        }
        // Also halt jq-common's flashTitle setInterval if it's running, so its
        // 1s tick doesn't immediately overwrite the cleared title.
        if (typeof window.stopFlashTitle === 'function') {
            window.stopFlashTitle();
        }
        if (baseTitle !== null) {
            render();
        }
    };

    document.addEventListener('ws:counter-update', (event) => {
        if (!event.detail || !TRACKED_COUNTER_IDS.includes(event.detail.id)) {
            return;
        }
        const value = parseInt(event.detail.value, 10);
        if (!Number.isFinite(value)) {
            return;
        }
        latestCount[event.detail.id] = value;

        if (isMessagesPage() || isUserLookingAtTab()) {
            // User is here — the badge stays empty and the next blur snapshots
            // these latest values as the baselines.
            return;
        }

        captureBaseTitle();
        snapshotBaselinesIfNeeded();
        render();
    });

    document.addEventListener('visibilitychange', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        } else {
            // Tab just went hidden — lock in the baselines so any subsequent
            // arrivals show as a delta from this moment.
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });

    // Window focus/blur covers the case where the tab is the front tab in a
    // background window — visibilityState stays "visible" but the user
    // isn't actually looking at it until the window gains focus.
    window.addEventListener('focus', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        }
    });

    window.addEventListener('blur', () => {
        if (!isUserLookingAtTab()) {
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });
})();

window["prepareNotification"] = function (entity) {
    // skip THREAD_SYNC_MESSAGE and FEED_SYNC_MESSAGE types
    if (entity.type === 'THREAD_SYNC_MESSAGE' || entity.type === 'FEED_SYNC_MESSAGE') {
        return null;
    }

    let data = entity.data,
        sender = entity.sender,
        cdn = '//ipstatic.net/';

    // default value When Sender is null...
    let thumb = cdn + 'images/male-empty-profile.png';

    let notification = {}

    if (sender) {
        if (sender.avatar) {
            thumb = sender.avatar.thumb_small;
        } else if (sender.sex) {
            thumb = cdn + 'images/' + sender.sex + '-empty-profile.png';
        }

        notification = {
            url: '/' + sender.username,
            username: sender.username,
            img_url: thumb,
            img_dims: "width='50' height='50'",
        };
    }

    /**
     * We don't use some notifications because it's rly hard to support in legacy:
     *
     * FEED_SYNC_MESSAGE
     * THREAD_SYNC_MESSAGE
     */
    switch (entity.type) {
        case 'COMMENT_MESSAGE':
            notification.title = window.t ? window.t('common.push_new_feed_comment') : 'New comment on your feed';
            notification.text = data.message;

            return notification;
        case 'COMMENT_REPLY_MESSAGE':
            notification.title = window.t ? window.t('common.push_comment_reply') : 'New reply to your comment';
            notification.text = data.message;

            return notification;
        case 'FRIEND_ACCEPT_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_accepted', {username: sender.username}) : sender.username + ' accepted your friendship!';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'BIRTHDAY_MESSAGE':
            notification.title = window.t ? window.t('common.push_birthday_today', {username: sender.username}) : sender.username + '`s birthday today';
            notification.text = window.t ? window.t('common.push_congratulate_birthday') : "Congratulate your friend on birthday!";

            return notification;
        case 'FRIEND_JOIN_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_joined', {username: sender.username}) : sender.username + ' just joined';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'FRIEND_REQUEST_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_request', {username: sender.username}) : sender.username + ' requested your friendship';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'LOGIN_NOTIFICATION':
            notification.title = window.t ? window.t('common.push_user_online', {username: sender.username}) : sender.username + ' is online';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'LIKE_MESSAGE':
            // @todo
            break;
        case 'PHOTO_COMMENT':
            notification.url = '/app/photo?pid=' + data.target_id;
            notification.title = window.t ? window.t('common.push_new_photo_comment', {username: sender.username}) : 'New photo comment from ' + sender.username;
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;
        case 'THREAD_NEW_MESSAGE':
            notification.url = '/app/messages?thread_id=' + data.thread_id;
            notification.thread_id = data.thread_id;
            notification.title = window.t ? window.t('common.push_new_message', {username: sender.username}) : 'New message from ' + sender.username;
            notification.text = data.snippet ? data.snippet : data.message;

            document.dispatchEvent(new CustomEvent('thread:newmessage', {
                detail: { thread_id: String(data.thread_id) }
            }));

            return notification;
        case 'THREAD_SYNC_DELETED':
            // A thread was deleted on another device, or removed because the
            // other user blocked us. Let the PM page drop the row in place.
            document.dispatchEvent(new CustomEvent('thread:deleted', {
                detail: { thread_id: String(data.thread_id) }
            }));
            return null;
        case 'THREAD_VIEWED':
            // @todo: handle in pm
            break;

        case 'THREAD_TYPING':
            document.dispatchEvent(new CustomEvent('thread:typing', {
                detail: { thread_id: String(data.thread_id), sender: sender }
            }));
            return null;

        case 'THREAD_MESSAGE_REACTION':
            // IDs kept as strings (they exceed Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:reaction', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id),
                    reactor_uid: String(data.reactor_uid),
                    reaction: data.reaction,
                    action: data.action,
                    reactions: data.reactions || []
                }
            }));
            return null;

        case 'THREAD_MESSAGE_RECALLED':
            // A message was unsent. Hand the PM page the ids so it can remove the
            // open bubble live. IDs stay strings (they exceed
            // Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:recalled', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id)
                }
            }));
            return null;

        case 'THREAD_MESSAGE_EDITED':
            // A message body was edited. Hand the PM page the server-rendered
            // body so it can swap the open bubble live. IDs stay strings (they
            // exceed Number.MAX_SAFE_INTEGER). `body` (raw editor prefill) is
            // present only on the sender's own channel.
            document.dispatchEvent(new CustomEvent('thread:edited', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id),
                    body_html: data.body_html || '',
                    body: (typeof data.body === 'string') ? data.body : null,
                    emoji_only: !!data.emoji_only,
                    edited_at: data.edited_at || ''
                }
            }));
            return null;

        case 'WALL_MESSAGE':
            notification.url = '/app/wall';
            notification.title = window.t ? window.t('common.push_new_wall_post') : 'New post on your wall';
            notification.img_url = data.thumb;
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;

        // 1:1 call lifecycle events. These drive the call UI directly (ring
        // modal, in-call overlay, thread buttons), never a passive toast, so
        // each re-dispatches a ws:call-* CustomEvent and returns null. Payload
        // fields sit at the top level of the socket message (see
        // CallSocketNotification / AbstractCallAuthNotification). IDs stay strings.
        case 'CALL_RING':
            document.dispatchEvent(new CustomEvent('ws:call-ring', {
                detail: {
                    call: entity.call,
                    caller: entity.caller,
                    livekit_url: entity.livekit_url,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_CANCEL':
            document.dispatchEvent(new CustomEvent('ws:call-cancel', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    reason: entity.reason,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_ACCEPT':
            document.dispatchEvent(new CustomEvent('ws:call-accept', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_ANSWERED_ELSEWHERE':
            document.dispatchEvent(new CustomEvent('ws:call-answered-elsewhere', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_DECLINE':
            document.dispatchEvent(new CustomEvent('ws:call-decline', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_END':
            document.dispatchEvent(new CustomEvent('ws:call-end', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    duration: entity.duration,
                    end_reason: entity.end_reason,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_AUTH_REQUEST':
            document.dispatchEvent(new CustomEvent('ws:call-auth-request', {
                detail: {
                    userId: entity.sender && entity.sender.id ? String(entity.sender.id) : null,
                    sender: entity.sender,
                }
            }));
            return null;

        case 'CALL_AUTH_ACCEPTED':
            document.dispatchEvent(new CustomEvent('ws:call-auth-accepted', {
                detail: {
                    userId: entity.sender && entity.sender.id ? String(entity.sender.id) : null,
                    sender: entity.sender,
                }
            }));
            return null;
    }

    return null;
}

window["escapeHtml"] = function (text) {
    var map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, function (m) {
        return map[m];
    });
}

// Write a single count into one badge element: sets the capped text and toggles
// visibility. Templates render the badge even at zero (hidden inline) so the
// count can appear/increment without a page refresh. Visibility uses an
// !important inline display so it wins over the collapsed/expanded CSS rules;
// clearing it lets those class rules pick the right variant again.
window["writeCounterBadge"] = function (badge, count) {
    badge.setAttribute('data-count', String(Math.max(0, count)));

    if (count > 0) {
        var cap = parseInt(badge.getAttribute('data-counter-cap') || '0', 10);
        badge.textContent = (cap > 0 && count > cap) ? (cap + '+') : String(count);
        var label = badge.getAttribute('aria-label');
        if (label) {
            badge.setAttribute('aria-label', label.replace(/^\d+/, String(count)));
        }
        badge.style.removeProperty('display');
    } else {
        badge.textContent = '';
        badge.style.setProperty('display', 'none', 'important');
    }
};

// Update every navigation badge bound to a counter id (e.g. 'pmNewCnt') to the
// new absolute value. Two kinds of badge opt in:
//   - data-counter="<id>"        a single counter (messages badge)
//   - data-counter-parts="a:2 b:0 ..."  an aggregate that sums several counters
//     (mobile "menu" badge). The changed counter's part is updated in place and
//     the badge shows the new sum, so the other components keep their values.
// data-counter-cap sets the optional "N+" ceiling (9 collapsed, 99 expanded).
window["syncCounterBadges"] = function (counterId, value) {
    var count = parseInt(value, 10);
    if (!Number.isFinite(count)) {
        return;
    }
    count = Math.max(0, count);

    document.querySelectorAll('[data-counter="' + counterId + '"]').forEach(function (badge) {
        writeCounterBadge(badge, count);
    });

    document.querySelectorAll('[data-counter-parts]').forEach(function (badge) {
        var parts = badge.getAttribute('data-counter-parts').trim().split(/\s+/).map(function (p) {
            var kv = p.split(':');
            return { id: kv[0], val: parseInt(kv[1], 10) || 0 };
        });
        var touched = false;
        var total = 0;
        parts.forEach(function (p) {
            if (p.id === counterId) {
                p.val = count;
                touched = true;
            }
            total += p.val;
        });
        if (!touched) {
            return;
        }
        badge.setAttribute('data-counter-parts', parts.map(function (p) {
            return p.id + ':' + p.val;
        }).join(' '));
        writeCounterBadge(badge, total);
    });
};

window["onMessage"] = function (wsMessage) {
    let data;
    try {
        data = JSON.parse(wsMessage.data);

        if (!data || typeof data !== 'object') {
            throw new Error('Invalid message format');
        }
    } catch (e) {
        wsLog('Failed to parse WebSocket message', e);
        return;
    }

    if (data.type === 'COUNTER_UPDATE') {
        // Server payload shape: { type: 'COUNTER_UPDATE', entity: { id, value } }
        const counterId = data.entity?.id;
        const counterValue = data.entity?.value;

        if (!counterId || typeof counterId !== 'string') {
            wsLog('Invalid COUNTER_UPDATE message - missing or invalid id');
            return;
        }

        if (TRACKED_COUNTER_IDS.includes(counterId)) {
            syncCounterBadges(counterId, counterValue);
            document.dispatchEvent(new CustomEvent('ws:counter-update', {
                detail: { id: counterId, value: counterValue }
            }));
        }
    } else if (data.type === 'GLOBAL_FEED_POST_ADDED') {
        document.dispatchEvent(new CustomEvent('ws:global-feed-post-added', {
            detail: data,
        }));
    } else {
        const notification = prepareNotification(data);

        if (notification && window.enable_notifs) {
            window.showNotifications([notification])
        }
    }
}

$(document).ready(function () {
    var retryCount = 0;
    var tokenRetryCount = 0;
    var isConnecting = false;
    var currentWs = null;

    var connect = async function () {
        if (isConnecting || currentWs) {
            wsLog('[WebSocket] Already connecting or connected, skipping');
            return false;
        }

        // Check if user is logged in by looking for user-id meta tag
        const userIdMeta = document.querySelector('meta[name="user-id"]');
        if (!userIdMeta || !userIdMeta.getAttribute('content')) {
            wsLog('[WebSocket] Not logged in, skipping connection');
            return false;
        }

        wsLog('[WebSocket] Starting connection attempt');
        isConnecting = true;

        var wspath = 'wss://api.interpals.net/v1/ws';

        if (document.location.hostname === 'localhost' || /^local(-\d+)?\.interpals\.net$/.test(document.location.hostname)) {
            var wsProto = document.location.protocol === 'https:' ? 'wss://' : 'ws://';
            wspath = wsProto + document.location.host + '/v1/ws';
        }

        // Get JWT token from localStorage
        const tokenData = await getOrInitializeJwtToken();
        if (!tokenData || !tokenData.auth || !tokenData.auth.token) {
            isConnecting = false;
            if (tokenRetryCount >= MAX_TOKEN_RETRIES) {
                wsLog('[WebSocket] Token not available after max retries, giving up');
                return false;
            }
            wsLog('[WebSocket] No token available yet, retrying in 1 second');
            tokenRetryCount++;
            setTimeout(function() {
                connect();
            }, 1000);
            return false;
        }

        tokenRetryCount = 0;
        wspath += '?token=' + tokenData.auth.token;

        var ws = new WebSocket(wspath);
        currentWs = ws;

        ws.onopen = function () {
            retryCount = 0;
            isConnecting = false;
            wsLog('[WebSocket] Connected successfully');
            subscribeToRequestedChannels(ws);
            document.dispatchEvent(new CustomEvent('ws:connected'));
        };

        ws.onmessage = onMessage;

        ws.onclose = function (event) {
            currentWs = null;
            isConnecting = false;
            document.dispatchEvent(new CustomEvent('ws:disconnected'));

            var retryTimeout = Math.min(Math.pow(2, retryCount), MAX_RETRY_DELAY);
            retryCount++;

            wsLog('[WebSocket] Connection closed (code: ' + event.code + '), retrying in ' + retryTimeout + ' seconds');

            setTimeout(
                function () {
                    connect()
                },
                retryTimeout * 1000
            );
        };

        ws.onerror = function (error) {
            wsLog('[WebSocket] Connection error', error);
            isConnecting = false;
        };
    };

    function cleanup() {
        if (currentWs) {
            wsLog('[WebSocket] Cleaning up connection');
            currentWs.close(1000, 'Page unload');
            currentWs = null;
        }
        isConnecting = false;
        retryCount = 0;
        tokenRetryCount = 0;
    }

    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);

    // A channel requested while this tab's socket is already open is subscribed
    // live. A channel requested before connect is covered by the
    // requestedWebSocketChannels set, which onopen flushes via
    // subscribeToRequestedChannels.
    document.addEventListener('ws:subscribe-channel-request', function (event) {
        const channel = event?.detail?.channel;

        if (channel && currentWs && currentWs.readyState === WebSocket.OPEN) {
            subscribeToChannel(currentWs, channel);
        }
    });

    connect();
});
